import React, { useEffect, useState } from "react";
import { X, ArrowRight, Clock } from "lucide-react";

// Promo end date — 30 days from Aug 14, 2026
const PROMO_END = new Date("2026-09-13T23:59:59");
const STORAGE_KEY = "jlc_promo_maintenance_closed_v1";

const PromoBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [countdown, setCountdown] = useState("");

  // Update CSS var so nav & hero can offset accordingly
  useEffect(() => {
    if (visible) {
      const h = expanded ? "220px" : "68px";
      document.documentElement.style.setProperty("--promo-h", h);
    } else {
      document.documentElement.style.setProperty("--promo-h", "0px");
    }
    return () => {
      document.documentElement.style.setProperty("--promo-h", "0px");
    };
  }, [visible, expanded]);

  useEffect(() => {
    // Show only if promo not expired and not closed by user
    try {
      const closed = sessionStorage.getItem(STORAGE_KEY);
      const now = new Date();
      if (!closed && now < PROMO_END) {
        setVisible(true);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const tick = () => {
      const diff = PROMO_END - new Date();
      if (diff <= 0) {
        setCountdown("Terminée");
        setVisible(false);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      if (days > 0) {
        setCountdown(`${days}j ${hours}h`);
      } else {
        const mins = Math.floor((diff / (1000 * 60)) % 60);
        setCountdown(`${hours}h ${mins}m`);
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [visible]);

  const close = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setVisible(false);
  };

  const goToDevis = () => {
    try {
      sessionStorage.setItem("preselectService", "Maintenance / Entretien");
      sessionStorage.setItem(
        "preselectMessage",
        "Bonjour, je suis intéressé(e) par votre offre spéciale « 2 maintenances effectuées = 1 offerte » (75€ pour 9000/12000 BTU, 110€ pour 18000/24000 BTU). Merci de me recontacter."
      );
      window.dispatchEvent(new Event("jlc:prefill"));
    } catch (e) {
      /* ignore */
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="promo-banner"
      className="fixed top-0 inset-x-0 z-[60] text-white shadow-[0_10px_40px_-15px_rgba(6,182,212,0.5)]"
      style={{
        background:
          "linear-gradient(100deg, #0f172a 0%, #059669 30%, #0891b2 65%, #06b6d4 100%)",
      }}
    >
      {/* Animated aurora shimmer */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute -inset-y-2 -left-1/4 w-1/2 opacity-30 blur-2xl"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
            animation: "sheen 8s linear infinite",
          }}
        />
      </div>

      {/* Left vertical accent line */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 bg-white/60"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-3.5">
        <div className="flex items-center justify-between gap-5 flex-wrap sm:flex-nowrap">
          {/* Left: number badge + main deal */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Big number 01 style */}
            <div className="hidden sm:flex flex-col items-start flex-shrink-0 leading-none">
              <span className="font-mono-num text-[9px] uppercase tracking-[0.28em] text-white/60 mb-0.5">
                No.01
              </span>
              <span
                className="font-serif-italic text-white text-4xl leading-none"
                style={{ fontFeatureSettings: "'ss01' on" }}
              >
                offre
              </span>
            </div>

            {/* Vertical divider */}
            <div className="hidden sm:block h-12 w-px bg-gradient-to-b from-transparent via-white/40 to-transparent flex-shrink-0" />

            <div className="min-w-0 flex-1">
              {/* Deal statement — mixed typography */}
              <div className="flex items-baseline gap-2 flex-wrap leading-tight">
                <span className="font-display font-black text-base sm:text-[19px] tracking-[-0.02em] text-white drop-shadow-sm">
                  2 maintenances
                </span>
                <span className="font-serif-italic text-white/95 text-lg sm:text-[22px] leading-none">
                  =
                </span>
                <span className="font-serif-italic text-white text-lg sm:text-[24px] leading-none">
                  1 offerte
                </span>
              </div>

              {/* Prices in mono */}
              <div className="hidden md:flex items-center gap-5 mt-1.5 text-[11.5px] uppercase tracking-[0.14em] text-white/85 font-semibold">
                <span className="flex items-baseline gap-2">
                  <span>9·12K BTU</span>
                  <span className="font-mono-num font-black text-white text-[15px]">75€</span>
                </span>
                <span className="text-white/40">/</span>
                <span className="flex items-baseline gap-2">
                  <span>18·24K BTU</span>
                  <span className="font-mono-num font-black text-white text-[15px]">110€</span>
                </span>
                <span className="text-white/40">/</span>
                <span className="flex items-center gap-1.5 text-white">
                  <Clock className="h-3 w-3" />
                  <span className="font-mono-num tabular-nums">{countdown}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: CTA + close */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setExpanded(!expanded)}
              className="md:hidden text-xs font-bold underline underline-offset-2 opacity-90 hover:opacity-100"
              aria-label="Voir les détails"
            >
              {expanded ? "Fermer" : "Détails"}
            </button>
            <a
              href="#devis-form"
              onClick={goToDevis}
              data-testid="promo-banner-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-white text-emerald-700 hover:bg-emerald-50 font-black uppercase text-[11px] tracking-[0.18em] px-5 py-2.5 rounded-full border-2 border-white transition-all hover:scale-[1.03] hover:shadow-[0_10px_25px_-5px_rgba(255,255,255,0.4)] group"
            >
              J&apos;en profite
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
            </a>
            <button
              onClick={close}
              data-testid="promo-banner-close"
              aria-label="Fermer le bandeau promotionnel"
              className="h-8 w-8 rounded-full grid place-items-center hover:bg-white/20 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Mobile expanded detail row */}
        {expanded && (
          <div className="md:hidden mt-3 pt-3 border-t border-white/20 flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-2 text-xs uppercase tracking-[0.14em] text-white/85 font-semibold">
              <span>9·12K BTU</span>
              <span className="font-mono-num font-black text-white text-base">75€</span>
            </div>
            <div className="h-px bg-white/15" />
            <div className="flex items-baseline justify-between gap-2 text-xs uppercase tracking-[0.14em] text-white/85 font-semibold">
              <span>18·24K BTU</span>
              <span className="font-mono-num font-black text-white text-base">110€</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold mt-1 text-white/95">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-mono-num">{countdown}</span>{" "}
              <span className="text-white/60">restants</span>
            </div>
            <a
              href="#devis-form"
              onClick={goToDevis}
              className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 font-black uppercase text-[11px] tracking-[0.18em] px-5 py-3 border-2 border-white rounded-full mt-1 w-fit shadow-lg"
            >
              J&apos;en profite <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>

      <style>{`
        @keyframes sheen {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
};

export default PromoBanner;
